
/**
 * Checks if a URL is a local/relative asset (e.g. /assets/avatar.png).
 * These should not be sent to the image-proxy-upload API.
 */
export const isLocalAssetUrl = (url: string | null | undefined): boolean => {
  if (!url || typeof url !== 'string') return false;
  const trimmed = url.trim();
  return trimmed.startsWith('/') && !trimmed.startsWith('//');
};

/**
 * Checks if a URL is from Google Cloud Storage (GCS)
 * Common GCS URL patterns:
 * - https://storage.googleapis.com/...
 * - https://*.storage.googleapis.com/...
 * - https://storage.cloud.google.com/...
 * - Custom domains pointing to GCS buckets
 */
export const isGCSUrl = (url: string | null | undefined): boolean => {
  if (!url) return false;
  
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();
    
    // Check for standard GCS domains
    return (
      hostname.includes('storage.googleapis.com') ||
      hostname.includes('storage.cloud.google.com') ||
      hostname.endsWith('.storage.googleapis.com')
    );
  } catch {
    // If URL parsing fails, assume it's not a GCS URL
    return false;
  }
};

/**
 * Gets a proxied image URL if the original URL is not from GCS.
 * Proxy API is not called; returns null for non-GCS so caller can show placeholder.
 * @param url - The original image URL
 * @returns The URL if GCS/local, or null to show placeholder
 */
export const getProxiedImageUrl = async (
  url: string | null | undefined,
  _postId: number,
  _mediaType: 'thumbnail' | 'profile_picture'
): Promise<string | null> => {
  // Return null if no URL provided
  if (!url) return null;
  
  // Don't proxy local assets (e.g. /assets/avatar.png)
  if (isLocalAssetUrl(url)) {
    return url;
  }
  
  // Return original URL if it's already from GCS
  if (isGCSUrl(url)) {
    return url;
  }
  
  // Do not call the proxy API; use placeholder for non-GCS URLs
  return null;
};

/**
 * Synchronous version that returns the original URL if not GCS
 * Use this when you need immediate rendering and can handle async loading separately
 * @param url - The original image URL
 * @returns The original URL (caller should handle proxying separately)
 */
export const getImageUrlSync = (url: string | null | undefined): string | null => {
  if (!url) return null;
  
  // Local assets and GCS URLs can be used as-is
  if (isLocalAssetUrl(url) || isGCSUrl(url)) {
    return url;
  }
  
  // Otherwise return original (caller should proxy async)
  return url;
};

